/**
 * O **contexto que um agente lê** antes de caçar preço.
 *
 * ## Por que isto é dado, e não prompt no código
 *
 * *"Prime ativo, frete grátis"*, *"PIX dá 5% off em muitas lojas"*, *"loja física
 * no Campo Comprido com retirada em 2h"* — nada disso é lógica do app: é o que
 * a **casa** sabe, e muda quando o Prime vence ou a loja fecha. Escrito no
 * código, mudar exigiria deploy; escrito aqui, é um campo de texto.
 *
 * É a contraparte do `recordedBy: 'ai'` do ponto de preço: aquele registra que
 * um agente **encontrou** algo, este é o que ele leu antes de procurar.
 *
 * ## Tudo é opcional, e a tela funciona vazia
 *
 * Nenhum campo é obrigatório e nenhum tem valor de fábrica com dado de
 * ninguém — endereço e cartão são da pessoa, e semear o repositório com eles
 * seria pôr dado pessoal no git.
 *
 * ## `toPrompt` é o que faz isto valer no dia em que sobe
 *
 * Sem ela, a tela seria um formulário que grava JSON que ninguém lê. Com ela,
 * o botão "copiar" entrega o contexto inteiro em markdown, pronto para colar
 * numa conversa — e no dia em que uma Function for chamar um agente, ela chama
 * a mesma função.
 */

export type PreferredSite = {
  /** `amazon.com.br`. */
  host: string
  name: string
  /** Uma vantagem por linha: "Prime ativo, frete grátis". */
  reasons: string[]
  /**
   * Oferta de primeira compra já gasta. Sem isto, o agente propõe um desconto
   * que não existe mais e o preço que ele achou é mentira.
   */
  firstPurchaseUsed: boolean
}

export type CashbackSite = {
  name: string
  host: string
  /** Livre: "varia por loja (ML 1,5% · Magalu 5%)". */
  rate: string
}

export type PaymentDiscount = {
  /** "PIX". */
  method: string
  note: string
}

export type Address = {
  street: string
  complement: string
  district: string
  city: string
  state: string
  cep: string
}

/** O que o agente aprendeu sobre navegar num site. */
export type SiteKnowledge = {
  host: string
  title: string
  baseUrl: string
  /** Um passo por linha. */
  steps: string[]
  /** Uma dica por linha. */
  tips: string[]
}

export type AgentInstructions = {
  /** O objetivo, em linguagem natural. Lido em toda busca. */
  goal: string
  preferredSites: PreferredSite[]
  cashbackSites: CashbackSite[]
  paymentDiscounts: PaymentDiscount[]
  address: Address
  /** Observação livre sobre busca local — raio, mapa, "vale ir ao centro". */
  localNote: string
  /** Por host. */
  siteKnowledge: SiteKnowledge[]
  updatedAt: string
}

export const INSTRUCTIONS_VAZIAS: AgentInstructions = {
  goal: '',
  preferredSites: [],
  cashbackSites: [],
  paymentDiscounts: [],
  address: { street: '', complement: '', district: '', city: '', state: '', cep: '' },
  localNote: '',
  siteKnowledge: [],
  updatedAt: '',
}

/**
 * O contexto inteiro em markdown, pronto para um agente ler.
 *
 * **Seção vazia não aparece.** Um cabeçalho "Sites preferidos" seguido de nada
 * gasta atenção do modelo dizendo que não há informação — e é justamente o tipo
 * de ruído que faz o resto ser lido com menos cuidado.
 */
/**
 * A casa preencheu alguma coisa?
 *
 * Existe porque `toPrompt` **deixou de poder ser vazia** quando o caminho de
 * volta entrou: ela sempre diz onde deixar recado, mesmo sem nada configurado.
 * A tela usava `prompt === ''` como sinal de "nada preenchido" — e esse sinal
 * quebrou em silêncio, com a prévia mostrando a instrução de recado como se
 * fosse contexto que alguém escreveu. A pergunta é sobre os DADOS, e agora ela
 * é feita aos dados.
 */
export function temContexto(i: AgentInstructions): boolean {
  return (
    i.goal.trim() !== '' ||
    i.localNote.trim() !== '' ||
    enderecoEmLinha(i.address) !== null ||
    i.preferredSites.length > 0 ||
    i.cashbackSites.length > 0 ||
    i.paymentDiscounts.length > 0 ||
    i.siteKnowledge.length > 0
  )
}

export function toPrompt(i: AgentInstructions): string {
  const partes: string[] = []

  if (i.goal.trim() !== '') partes.push(i.goal.trim())

  const endereco = enderecoEmLinha(i.address)
  if (endereco !== null || i.localNote.trim() !== '') {
    const linhas = ['## Onde eu estou']
    if (endereco !== null) linhas.push(endereco)
    if (i.localNote.trim() !== '') linhas.push(i.localNote.trim())
    partes.push(linhas.join('\n'))
  }

  if (i.preferredSites.length > 0) {
    partes.push(
      [
        '## Sites preferidos',
        ...i.preferredSites.map((s) => {
          const motivos = s.reasons.filter((r) => r.trim() !== '')
          // A oferta gasta é dita na mesma linha do site, e não numa seção
          // separada: quem lê o site precisa da ressalva ali, não depois.
          const gasta = s.firstPurchaseUsed ? ' — oferta de primeira compra JÁ USADA' : ''
          return `- **${s.name || s.host}** (${s.host})${gasta}${
            motivos.length > 0 ? `\n${motivos.map((r) => `  - ${r}`).join('\n')}` : ''
          }`
        }),
      ].join('\n'),
    )
  }

  if (i.cashbackSites.length > 0) {
    partes.push(
      [
        '## Cashback (consultar antes de comprar)',
        ...i.cashbackSites.map(
          (c) => `- **${c.name || c.host}** (${c.host})${c.rate.trim() === '' ? '' : ` — ${c.rate}`}`,
        ),
      ].join('\n'),
    )
  }

  if (i.paymentDiscounts.length > 0) {
    partes.push(
      [
        '## Descontos por forma de pagamento',
        ...i.paymentDiscounts.map((p) => `- **${p.method}**: ${p.note}`),
      ].join('\n'),
    )
  }

  if (i.siteKnowledge.length > 0) {
    partes.push(
      [
        '## Navegação aprendida',
        ...i.siteKnowledge.map((s) => {
          const bloco = [`### ${s.title || s.host}`, s.baseUrl.trim() === '' ? null : s.baseUrl]
          const passos = s.steps.filter((x) => x.trim() !== '')
          const dicas = s.tips.filter((x) => x.trim() !== '')
          if (passos.length > 0) bloco.push('Passos:', ...passos.map((x, n) => `${String(n + 1)}. ${x}`))
          if (dicas.length > 0) bloco.push('Dicas:', ...dicas.map((x) => `- ${x}`))
          return bloco.filter((x) => x !== null).join('\n')
        }),
      ].join('\n\n'),
    )
  }

  // **Sempre a última, e sempre presente.** As outras seções descrevem o que a
  // casa sabe e somem quando estão vazias; esta descreve o que fazer com o que
  // for encontrado, e não depende de a pessoa ter preenchido nada. Sem ela o
  // contexto era só de ida — o agente lia e não tinha onde devolver.
  partes.push(COMO_DEIXAR_RECADO)

  return partes.join('\n\n')
}

/**
 * O caminho de volta.
 *
 * O resto deste arquivo é o que o agente **lê**; isto é o que ele **escreve**.
 *
 * ## O agente escreve SOZINHO quando tem navegador
 *
 * A primeira versão disto assumia que a pessoa colava o texto — *"o app não tem
 * API para agente"*. Não tem, e não precisa: um agente com controle do navegador
 * entra na sessão que já está aberta e usa a tela como qualquer pessoa usaria.
 * Foi assim que o primeiro recado desta funcionalidade foi escrito, no dia em
 * que ela subiu.
 *
 * Por isso o texto abaixo tem **dois caminhos**, e o de colar é o de quem não
 * tem navegador — e não o principal.
 *
 * ## E é por isso que a autoria é uma DECLARAÇÃO
 *
 * O agente escreve com a **credencial da pessoa** — não existe login de agente
 * neste app. `createdBy` vai ser o uid dela, e nenhuma regra pode desmentir
 * isso. Assinar "um agente de IA" é o que mantém o registro honesto, e é uma
 * escolha de etiqueta, não uma verificação. Ver `Recado`.
 *
 * ## Pede link e data porque é o que apodrece
 *
 * Um preço sem link não dá para conferir depois, e sem data não dá para saber se
 * ainda vale. É a mesma razão do `sourceUrl` no ponto de preço: *"o `merchantId`
 * diz onde; este diz em qual página, que é o que permite voltar lá e conferir"*.
 */
const COMO_DEIXAR_RECADO = [
  '## Como me devolver o que você achar',
  '',
  'O Porquinho tem **recados**, na tela de Notificações. É onde eu leio o que',
  'você encontrou.',
  '',
  '### Se você controla o navegador, escreva você mesmo',
  '',
  'É o caminho preferido — não me faça copiar e colar o que você já sabe.',
  '',
  '1. Abra `https://porquinho.rilo.cc/notificacoes` (minha sessão já está',
  '   aberta; você não precisa de login).',
  '2. Toque no “+” ao lado do título.',
  '3. Escreva o recado no campo de texto.',
  '4. Em “Quem escreveu”, escolha **Um agente de IA** e ponha o seu nome.',
  '5. “Deixar o recado”.',
  '',
  'O passo 4 não é opcional. Você está escrevendo com a MINHA credencial — não',
  'existe login de agente aqui —, então o sistema vai registrar que fui eu quem',
  'gravou. A etiqueta é a única coisa que diz que o conteúdo é seu, e é dela que',
  'depende eu saber quanta confiança dar ao que está escrito. Assinar como se',
  'fosse eu seria mentir num registro que eu vou reler daqui a um mês.',
  '',
  '### Se você não controla o navegador, me dê pronto',
  '',
  'Termine sua resposta com um bloco que eu colo sem editar:',
  '',
  '```',
  'RECADO',
  '<uma linha dizendo o que você foi procurar>',
  '- <loja> — <preço> — <link completo> — visto em <data>',
  '- ...',
  '<o que você achou digno de nota: frete, prazo, condição do cupom, ressalva>',
  '```',
  '',
  '### O que o recado precisa ter, nos dois casos',
  '',
  '- **Link completo e clicável**, sempre. Preço sem link não dá para conferir',
  '  depois, e um preço que não dá para conferir não vale nada aqui.',
  '- **Diga a data** em que você viu. Preço envelhece.',
  '- **Diga o que você NÃO conseguiu confirmar.** "O cupom pode ter expirado" é',
  '  informação; um preço apresentado como certo que não era é pior que nenhum.',
  '- Um recado por busca. Se você procurou três coisas, três recados.',
].join('\n')

/** `null` quando não há endereço nenhum — e não uma linha de vírgulas soltas. */
function enderecoEmLinha(a: Address): string | null {
  const pedacos = [
    [a.street, a.complement].filter((x) => x.trim() !== '').join(', '),
    a.district,
    [a.city, a.state].filter((x) => x.trim() !== '').join('/'),
    a.cep,
  ].filter((x) => x.trim() !== '')
  return pedacos.length === 0 ? null : pedacos.join(' · ')
}
